import type { MessageThreadEvent } from "@/types/threadEvents.ts";
import { atom } from "jotai";

export const messageListScrollPositionAtom = atom(-1);
export const categoryScrollPositionAtom = atom(-1);

/**
 * Optimistic user messages handed off from a thread-creating view (e.g. the
 * home-page OmniBox) to the thread it navigates to. The thread's
 * `useSessionChat` reads its entry on mount, seeds it into the feed so the
 * message renders instantly (no empty-feed flash), and clears it. Keyed by
 * threadId. The placeholder is keyed on the `tempClientId` we minted, so the
 * `idToClientId` socket event reconciles it once the (already-mounted) thread
 * page is subscribed.
 */
export const pendingThreadMessagesAtom = atom<Record<number, MessageThreadEvent>>({});

/**
 * Transient per-file status for an in-flight attachment send, shown as a chip
 * overlay on the user message until the real file artifacts arrive.
 *
 *   uploading → bytes are going to GCS (client-driven)
 *   analyzing → uploaded + message POSTed; the `summarizeFile` job is running
 *   error     → the client-side upload or POST failed (offer Retry)
 *
 * The backend's own summarization *failure* is NOT represented here — it lands
 * as a `status:"failed"` file artifact on the message. This atom only covers the
 * pre-artifact window. Kept separate from `pendingThreadMessagesAtom` because
 * that handoff is consumed+cleared on mount, whereas this status must persist
 * and mutate across upload → analyze → reconcile.
 */
export type PendingAttachmentStatus = "uploading" | "analyzing" | "error";

export interface PendingAttachmentFile {
  /** Stable client id (AttachedFile.id), used as the React key. */
  id: string;
  name: string;
  /** Image preview dataURL, when the attachment is an image. */
  preview?: string;
  status: PendingAttachmentStatus;
  errorMessage?: string;
}

export interface PendingAttachmentState {
  /** Ties this overlay to the user message via `clientOnly.clientChatId`. */
  tempClientId: number;
  files: PendingAttachmentFile[];
  /** Re-runs the whole upload+send. Present once a send has failed. */
  retry?: () => void;
}

export const pendingThreadAttachmentsAtom = atom<Record<number, PendingAttachmentState>>({});
